import { useEffect, useRef, useState } from "react";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { useStore } from "../root-store-context";
import { GENERIC_MESSAGE, GuestTokenResult } from "../api/Report";

const MAX_RETRIES = 2;
const RETRY_BASE_MS = 800;
const NOT_CONFIGURED_MESSAGE = "Reporting isn't configured for this environment.";

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const looksLikeJwt = (t: string) => {
    const parts = t.split(".");
    return parts.length === 3 && parts.every((s) => s.length > 0);
};

const isTransient = (status: number) => status === 0 || status >= 500;


export const useSupersetDashboard = (dashboardId: string, mountId: string) => {
    const { reportStore, applicationStore: { SelectedAddress } } = useStore();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const lastUsedCommunityId = useRef<string | null>(null);
    const embedHandle = useRef<{ unmount: () => void } | null>(null);

    useEffect(() => {
        const communityId = SelectedAddress?.communityId;

        if (!communityId) return;
        if (communityId === lastUsedCommunityId.current) return;
        lastUsedCommunityId.current = communityId;

        setIsLoading(true);
        setLoadError(null);

        let cancelled = false;

        let runLastToken: string | null = null;
        let embedded = false;

        const domain = reportStore.getReportUrl();
        if (!domain) {
            setLoadError(NOT_CONFIGURED_MESSAGE);
            setIsLoading(false);
            return;
        }

        const fetchWithRetry = async (): Promise<GuestTokenResult> => {
            let res = await reportStore.getGuestToken(dashboardId, communityId);
            for (let attempt = 1; attempt <= MAX_RETRIES && !res.isSuccess && isTransient(res.status); attempt++) {
                await delay(RETRY_BASE_MS * attempt);
                res = await reportStore.getGuestToken(dashboardId, communityId);
            }
            if (res.isSuccess && !looksLikeJwt(res.token)) {
                return { isSuccess: false, message: GENERIC_MESSAGE, status: 0 };
            }
            return res;
        };

        const fetchGuestToken = async (): Promise<string> => {
            if (cancelled) return runLastToken ?? "";

            const res = await fetchWithRetry();
            if (cancelled) return runLastToken ?? "";

            if (res.isSuccess) {
                runLastToken = res.token;
                return res.token;
            }

            if (embedded) {
                console.warn(`Superset guest token refresh failed: ${res.message}`);
                return runLastToken ?? "";
            }
            setLoadError(res.message);
            return runLastToken ?? "";
        };

        (async () => {
            const first = await fetchWithRetry();
            if (cancelled) return;

            if (!first.isSuccess) {
                setLoadError(first.message);
                setIsLoading(false);
                return;
            }
            runLastToken = first.token;

            try {
                embedHandle.current?.unmount();
                embedHandle.current = null;

                const mount = document.getElementById(mountId);
                if (!mount) {
                    setLoadError(GENERIC_MESSAGE);
                    setIsLoading(false);
                    return;
                }

                embedded = true;

                const handle = await embedDashboard({
                    id: dashboardId,
                    supersetDomain: domain,
                    mountPoint: mount as HTMLElement,
                    fetchGuestToken,
                    dashboardUiConfig: {
                        hideTitle: true,
                        hideChartControls: false,
                        hideTab: false,
                        filters: {
                            expanded: true,
                        },
                    },
                });

                if (cancelled) {
                    handle.unmount();
                    return;
                }
                embedHandle.current = handle;
            } catch {
                if (!cancelled) setLoadError(GENERIC_MESSAGE);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [SelectedAddress?.communityId, dashboardId, mountId, reportStore]);

    useEffect(() => () => {
        embedHandle.current?.unmount();
        embedHandle.current = null;
    }, []);

    return { isLoading, loadError };
};

export default useSupersetDashboard;
